import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Combobox } from "@/src/components/form-fields/Combobox";
import { Input } from "@/src/components/ui/input";
import { Textarea } from "@/src/components/ui/textarea";
import { findFamilyByUuid, FONT_SCOPE_ORDER_LIST } from "@/src/utils/fontFaces";

import type { ChangeEvent, CSSProperties, JSX } from "react";

import type {
  FontFamily,
  FontRole,
  FontScope,
  ProjectFontRoles,
  ProjectStyles,
} from "@/src/api/StylesAPI.types";
import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

const GROUP_KEY_BY_SCOPE: Record<FontScope, string> = {
  system: "systemFonts",
  catalog: "okastCatalog",
  organization: "myFonts",
};

interface RoleField {
  role: FontRole;
  labelKey: string;
  sampleKey: string;
  sampleClassName: string;
  sampleRows: number;
  testId: string;
}

const ROLE_FIELD_LIST: RoleField[] = [
  {
    role: "title",
    labelKey: "titleRole",
    sampleKey: "previewTitle",
    sampleClassName:
      "font-title h-auto bg-muted px-3.5 py-3 text-xl font-semibold md:text-xl dark:bg-muted",
    sampleRows: 1,
    testId: "font-sample-title",
  },
  {
    role: "text",
    labelKey: "textRole",
    sampleKey: "previewText",
    sampleClassName:
      "font-text field-sizing-fixed resize-none bg-muted px-3.5 py-3 text-sm leading-relaxed dark:bg-muted",
    sampleRows: 3,
    testId: "font-sample-text",
  },
];

interface FontRoleFieldProps {
  field: RoleField;
  isLoading: boolean;
  isDisabled: boolean;
  optionList: ComboboxOption[];
  renderOption: (option: ComboboxOption) => JSX.Element;
}

function FontRoleField({
  field,
  isLoading,
  isDisabled,
  optionList,
  renderOption,
}: FontRoleFieldProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");

  // Bloc-notes local : le texte saisi sert à juger la police, il n'est jamais enregistré.
  const [typedSampleText, setTypedSampleText] = useState<string | null>(null);

  const handleSampleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setTypedSampleText(event.target.value);
  };

  const sampleLabel = t("sampleAriaLabel", { role: t(field.labelKey) });
  const sampleText = typedSampleText ?? t(field.sampleKey);

  return (
    <div className="flex flex-col gap-[7px]">
      <Combobox<ProjectStyles>
        disabled={isDisabled}
        emptyLabel={t("noFontFound")}
        isLoading={isLoading}
        label={t(field.labelKey)}
        loadingLabel={t("loadingFonts")}
        name={`fonts.${field.role}`}
        options={optionList}
        placeholder={t("selectFont")}
        renderOption={renderOption}
        searchPlaceholder={t("searchFont")}
      />
      {field.sampleRows > 1 ? (
        <Textarea
          aria-label={sampleLabel}
          className={field.sampleClassName}
          data-testid={field.testId}
          onChange={handleSampleChange}
          rows={field.sampleRows}
          value={sampleText}
        />
      ) : (
        <Input
          aria-label={sampleLabel}
          className={field.sampleClassName}
          data-testid={field.testId}
          onChange={handleSampleChange}
          type="text"
          value={sampleText}
        />
      )}
    </div>
  );
}

interface FontRoleFieldsProps {
  familyList: FontFamily[];
  roles: ProjectFontRoles;
  isLoading: boolean;
  isDisabled: boolean;
}

function FontRoleFields({
  familyList,
  roles,
  isLoading,
  isDisabled,
}: FontRoleFieldsProps): JSX.Element {
  const { t } = useTranslation("editor-appearance");

  const optionList: ComboboxOption[] = useMemo(
    () =>
      FONT_SCOPE_ORDER_LIST.flatMap((scope) =>
        familyList
          .filter((family) => family.scope === scope)
          .map((family) => ({
            label: family.display_name,
            value: family.uuid,
            group: t(GROUP_KEY_BY_SCOPE[scope]),
          })),
      ),
    [familyList, t],
  );

  const renderOption = (option: ComboboxOption): JSX.Element => (
    <span
      style={{
        fontFamily: findFamilyByUuid(familyList, option.value)?.css_stack,
      }}
    >
      {option.label}
    </span>
  );

  // Convention partagée avec le storefront : la variable porte la valeur, l'utilitaire la consomme.
  const sampleStyle = {
    "--font-title": findFamilyByUuid(familyList, roles.title)?.css_stack,
    "--font-text": findFamilyByUuid(familyList, roles.text)?.css_stack,
  } as CSSProperties;

  return (
    <div
      className="flex flex-col gap-[22px]"
      data-testid="font-sample-scope"
      style={sampleStyle}
    >
      {ROLE_FIELD_LIST.map((field) => (
        <FontRoleField
          key={field.role}
          field={field}
          isDisabled={isDisabled}
          isLoading={isLoading}
          optionList={optionList}
          renderOption={renderOption}
        />
      ))}
    </div>
  );
}

export default FontRoleFields;
